/**
 * 크기 계산을 위한 유틸리티 클래스
 * 원본 크기와 참조 크기를 기반으로 최소/최대 크기를 계산합니다.
 */
export interface Size {
  width: number;
  height: number;
}

export interface SizeRange {
  min_width: number;
  min_height: number;
  max_width: number;
  max_height: number;
}

export const MAX_DIMENSION = 90;
export const MIN_DIMENSION = 25;

export class CalculatorSize {
  /**
   * 주어진 크기가 지정된 범위 내에 있도록 조정합니다.
   * @param size 조정할 크기 (width, height)
   * @param size_range 크기 범위 (min_width, min_height, max_width, max_height)
   * @param maintain_ratio 비율을 유지할지 여부
   * @returns 조정된 크기 (width, height)
   */
  static adjustSizeRange(size: Size, size_range: SizeRange): Size {
    let { width, height } = size; // 값을 변경할 수 있도록 let으로 받습니다.
    const { min_width, min_height, max_width, max_height } = size_range;

    const original_ratio = width / height;

    // 최대 크기 제한: 너비나 높이 중 하나라도 최대치를 초과하는 경우
    if (width > max_width || height > max_height) {
      // 너비를 최대치로 맞췄을 때의 높이를 계산
      const height_at_max_width = max_width / original_ratio;

      // 그 계산된 높이가 최대 높이보다 크다면, 높이가 기준이 되어야 함
      if (height_at_max_width > max_height) {
        height = max_height;
        width = height * original_ratio;
      } else {
        // 너비가 기준이 됨
        width = max_width;
        height = width / original_ratio;
      }
    }

    // 최소 크기 제한: 너비나 높이 중 하나라도 최소치 미만인 경우
    if (width < min_width || height < min_height) {
      // 너비를 최소치로 맞췄을 때의 높이를 계산
      const height_at_min_width = min_width / original_ratio;

      // 그 계산된 높이가 최소 높이보다 작다면, 높이가 기준이 되어야 함
      if (height_at_min_width < min_height) {
        height = min_height;
        width = height * original_ratio;
      } else {
        // 너비가 기준이 됨
        width = min_width;
        height = width / original_ratio;
      }
    }

    return { width, height };
  }

  /**
   * 슬라이더 값에 따라 새로운 크기를 계산합니다.
   * @param sliderValue 슬라이더 값 (0.0 ~ 1.0)
   */
  static calculateSizeBySlider(sliderValue: number, sizeRange: SizeRange): Size {
    const boundedValue = Math.max(0, Math.min(1, sliderValue));
    const { min_width, max_width, min_height, max_height } = sizeRange;

    const widthRange = max_width - min_width;

    const newWidth = min_width + widthRange * boundedValue;

    const heightRange = max_height - min_height;

    const newHeight = min_height + heightRange * boundedValue;

    return {
      width: Math.round(newWidth),
      height: Math.round(newHeight),
    };
    // return CalculatorSize.adjustSizeRange({ width: new_width, height: new_height }, size_range);
  }

  /**
   * 현재 크기가 범위 내에서 어느 위치에 있는지 슬라이더 값 계산
   * @param current_size 현재 크기 (width, height)
   * @param size_range 크기 범위 (min_width, min_height, max_width, max_height)
   * @returns 슬라이더 값 (0.0 ~ 1.0)
   */
  static calculateSliderValue(current_size: Size, size_range: SizeRange): number {
    const { width, height } = current_size;
    const { min_width, min_height, max_width, max_height } = size_range;

    // 현재 너비와 높이가 최소/최대 범위 내에 있는지 확인
    const current_width = Math.max(min_width, Math.min(max_width, width));
    const current_height = Math.max(min_height, Math.min(max_height, height));

    // 너비와 높이의 현재 비율 계산
    const width_div = max_width - min_width;
    const height_div = max_height - min_height;

    const width_ratio = width_div === 0 ? 0 : (current_width - min_width) / width_div;
    const height_ratio = height_div === 0 ? 0 : (current_height - min_height) / height_div;

    // 두 비율의 평균 반환
    return Math.max(0, Math.min(1, (width_ratio + height_ratio) / 2));
  }

  static sliderValuesToSize(init_size: Size, size: Size, size_range: SizeRange) {
    const init_value = CalculatorSize.calculateSliderValue(
      { width: init_size.width, height: init_size.height },
      size_range,
    );

    const slider_value = CalculatorSize.calculateSliderValue(
      { width: size.width, height: size.height },
      size_range,
    );

    return {
      ...(init_value !== undefined && { init_value }),
      ...(slider_value !== undefined && { slider_value }),
    };
  }

  static checkOverflow(size: Size, sizeRange: SizeRange) {
    const { width, height } = size;
    const { min_width, max_width, min_height, max_height } = sizeRange;

    // 넘치는 경우 (max)
    if (width > max_width || height > max_height) {
      return { size: { width: max_width, height: max_height }, slider_value: 1 };
    }

    // 작은 경우 (min)
    if (width < min_width || height < min_height) {
      return { size: { width: min_width, height: min_height }, slider_value: 0 };
    }

    return undefined;
  }
}
